import maxBy from "lodash.maxby";

import {
  ConditionType,
  RateConditionType,
  RateGroupType,
  ShopifyCurrencyType,
  VariationDto,
} from "@intelligems/ig-types";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import { groupRounder } from "@intelligems/ig-utils";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { getVariationFeFromDto } from "@src/api/formatters/from-dto/variations";
import {
  initialShippingRateGroupsState,
  initialSingleVariationState,
  initialVariation,
  initialVariationState,
} from "@src/api/initials";
import {
  ShippingRateGroupReducer,
  ShippingRateGroupReducerAction,
} from "@src/api/reducers/shippingRateGroupReducer";
import { orderFlatThresholdRate } from "@src/features/experiments/pages/shippingExperience/components/RateBuilder/util";
import { IVariationsState } from "@src/store/types/variations";
import { sortByOrder } from "@src/utils/sortings";

const initialState: IVariationsState = {
  isSuccess: false,
  variations: [],
  storedVariations: [],
};

const slice = createSlice({
  name: "variations",
  initialState,
  reducers: {
    resetVariations: (_) => initialState,
    resetSingleVariationPriceChange: (draft) => {
      draft.variations[0].priceChange = undefined;
      draft.variations[0].priceChangeUnit = "percent";
    },
    initVariations: (
      draft,
      action: PayloadAction<{
        variations: VariationDto[] | undefined;
        isPersonalization?: boolean;
      }>
    ) => {
      const { variations, isPersonalization } = action.payload;

      draft.isSuccess = true;
      if (variations) {
        draft.variations = getVariationFeFromDto(variations);
      } else {
        if (isPersonalization) {
          draft.variations = [initialSingleVariationState()];
        } else {
          draft.variations = initialVariationState();
        }
      }
      draft.storedVariations = draft.variations;
    },
    setVariations: (draft, action: PayloadAction<VariationFe[]>) => {
      if (draft.variations) {
        draft.variations = action.payload;
      }
    },
    variationKeyChange: (
      draft,
      action: PayloadAction<{ index: number; value: string; key: string }>
    ) => {
      const { index, key, value } = action.payload;

      draft.variations[index][key] = value;
    },
    setTestExperienceId: (
      draft,
      action: PayloadAction<{ variationId: string; testExperienceId?: string }>
    ) => {
      const { variationId, testExperienceId } = action.payload;

      const variation = draft.variations.find((v) => v.id === variationId);
      if (!variation) return;

      variation.testExperienceId = testExperienceId;
    },
    toggleTierType: (
      draft,
      action: PayloadAction<{ selectedTierType: RateConditionType }>
    ) => {
      const { selectedTierType } = action.payload;

      for (const variation of draft.variations) {
        for (const group of variation.shippingRateGroups ?? []) {
          for (const rate of group.rates) {
            if (group.groupType !== "tieredByPriceOrWeight") {
              rate.condition = undefined;
            } else if (rate.condition) {
              rate.condition = {
                ...rate.condition,
                conditionType: selectedTierType,
              };
            }
          }
        }
      }
    },
    setAllRatesCurrency: (
      draft,
      action: PayloadAction<{ currency: string }>
    ) => {
      const { currency } = action.payload;

      for (const variation of draft.variations) {
        for (const group of variation.shippingRateGroups ?? []) {
          for (const rate of group.rates) {
            rate.currency = currency as ShopifyCurrencyType;
          }
        }
      }
    },
    dispatchShippingRateGroups: (
      draft,
      action: PayloadAction<{
        reducer: ShippingRateGroupReducerAction;
        variationIndex: number;
      }>
    ) => {
      if (!draft.isSuccess) return;
      const { reducer, variationIndex } = action.payload;

      draft.variations[variationIndex] = ShippingRateGroupReducer(
        draft.variations[variationIndex],
        reducer
      )!;
    },
    initializeRateType: (
      draft,
      action: PayloadAction<{
        shippingRateGroupType: RateGroupType;
        storeCurrencyCode: ShopifyCurrencyType;
        variationId: string;
        tierType: RateConditionType;
        unitType: ConditionType;
      }>
    ) => {
      if (!draft.isSuccess) return;

      const { variationId } = action.payload;
      const variation = draft.variations.find((v) => v.id === variationId);
      if (!variation) return;

      variation.shippingRateGroups = [
        initialShippingRateGroupsState(
          action.payload.shippingRateGroupType,
          action.payload.storeCurrencyCode,
          action.payload.tierType,
          action.payload.unitType,
          0,
          variationId
        ),
      ];
    },
    addShippingRate: (
      draft,
      action: PayloadAction<{
        variationId: string;
        shippingRateGroupType: RateGroupType;
        storeCurrencyCode: ShopifyCurrencyType;
        tierType: RateConditionType;
        unitType: ConditionType;
      }>
    ) => {
      if (!draft.isSuccess) return;

      const variation = draft.variations.find(
        (v) => v.id === action.payload.variationId
      );
      if (!variation) return;

      variation.shippingRateGroups!.push(
        initialShippingRateGroupsState(
          action.payload.shippingRateGroupType,
          action.payload.storeCurrencyCode,
          action.payload.tierType,
          action.payload.unitType,
          variation.shippingRateGroups!.length,
          action.payload.variationId
        )
      );
    },
    removeShippingRate: (
      draft,
      action: PayloadAction<{
        variationId: string;
        shippingRateGroupIndex: number;
      }>
    ) => {
      if (!draft.isSuccess) return;

      const { variationId, shippingRateGroupIndex } = action.payload;

      const variation = draft.variations.find((v) => v.id === variationId);
      if (!variation?.shippingRateGroups) return;

      variation.shippingRateGroups = variation.shippingRateGroups
        .filter((_, index) => index !== shippingRateGroupIndex)
        .map((group, index) => ({ ...group, position: index }));
    },
    deleteVariation: (draft, action: PayloadAction<string>) => {
      if (!draft.isSuccess) return;

      draft.variations = groupRounder(
        draft.variations
          .filter((v) => v.id !== action.payload)
          .map((v, index) => ({
            ...v,
            order: index,
            percentage: 100 / (draft.variations.length - 1),
          }))
      );
    },
    addVariation: (draft, action: PayloadAction<string>) => {
      if (!draft.isSuccess) return;
      const id = action.payload;

      const maxOrder = maxBy(draft.variations, "order")?.order || 0;
      draft.variations.push(initialVariation(id, maxOrder, draft.variations));
      draft.variations.forEach((v) => {
        v.percentage = 100 / draft.variations.length;
      });
      draft.variations = groupRounder(draft.variations);
    },
    reorderVariations: (
      draft,
      action: PayloadAction<{ startIndex: number; endIndex: number }>
    ) => {
      if (!draft.isSuccess) return;

      const [removed] = draft.variations.splice(action.payload.startIndex, 1);
      draft.variations.splice(action.payload.endIndex, 0, removed);
      draft.variations.forEach((v, index) => (v.order = index));
      draft.variations = draft.variations.sort(sortByOrder);
    },
    variationSlide: (draft, action: PayloadAction<Record<string, number>>) => {
      if (!draft.isSuccess) return draft;

      for (const variation of draft.variations) {
        variation.percentage = action.payload[variation.id] ?? 0;
      }
    },
    ensureRateGroupOrder: (draft) => {
      if (!draft.isSuccess) return;

      draft.variations.forEach((variation) => {
        if (variation.shippingRateGroups) {
          variation.shippingRateGroups.forEach((rateGroup) =>
            rateGroup.rates.sort(orderFlatThresholdRate)
          );
        }
      });
    },
  },
  selectors: {
    selectedVariationsState: (state) => state,
    selectedVariations: (state) => state.variations || [],
    getControlGroup: (state) => state.variations?.find((v) => v.isControl),
  },
});

export const {
  resetVariations,
  initVariations,
  setVariations,
  dispatchShippingRateGroups,
  initializeRateType,
  removeShippingRate,
  addVariation,
  addShippingRate,
  toggleTierType,
  deleteVariation,
  variationKeyChange,
  variationSlide,
  reorderVariations,
  ensureRateGroupOrder,
  setTestExperienceId,
  resetSingleVariationPriceChange,
  setAllRatesCurrency,
} = slice.actions;

export const variationsReducer = slice.reducer;
export const { selectedVariationsState, selectedVariations, getControlGroup } =
  slice.selectors;
